import apiClient, { storage, AUTH_KEYS } from './apiClient';
import { API_BASE_URL } from '../config/apiConfig';

// File service — web version of r2b-restaurant-mobile-new/src/services/fileService.js.
// Uses browser-native File + FormData + fetch for uploads.

export const fileService = {
  testConnection: async () => {
    try {
      const response = await apiClient.get('/files?limit=1');
      return { success: true, status: response.status };
    } catch (error) {
      return {
        success: false,
        status: error.response?.status,
        message: error.message,
        isAuthError: error.response?.status === 401,
        isPermissionError: error.response?.status === 403,
      };
    }
  },

  /**
   * Download a file by its ID. Returns the resolvable URL string (or null).
   */
  downloadFile: async (id) => {
    try {
      const response = await apiClient.get(`/download/${id}`);
      return response.data.data;
    } catch {
      return null;
    }
  },

  /**
   * Download many files in parallel, returning resolved URLs (falsy ones filtered).
   */
  downloadMultipleFiles: async (ids) => {
    if (!ids || !Array.isArray(ids)) return [];
    const validIds = ids.filter(Boolean);
    const uris = await Promise.all(validIds.map((id) => fileService.downloadFile(id)));
    return uris.filter(Boolean);
  },

  /**
   * Upload a browser-native File (from an <input type="file" />) to /upload.
   * Returns { success, data } with the created file document (includes `_id`).
   */
  uploadFile: async (file, entityType = 'food-item', entityName = 'menu') => {
    if (!(file instanceof File) && !(file instanceof Blob)) {
      throw new Error('uploadFile: `file` must be a File or Blob');
    }

    const formData = new FormData();
    formData.append('file', file, file.name || 'upload');
    formData.append('entityType', entityType);
    formData.append('entityName', entityName);

    const uploadUrl = `${API_BASE_URL}/upload`;
    const token = storage.get(AUTH_KEYS.token);

    // Note: we purposely do NOT set Content-Type. The browser will add a proper
    // `multipart/form-data; boundary=...` header automatically.
    const response = await fetch(uploadUrl, {
      method: 'POST',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'ngrok-skip-browser-warning': 'true',
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${response.status} ${errorText}`);
    }

    const result = await response.json();
    if (result && result.success && result.data && result.data._id) {
      return { success: true, data: result.data };
    }
    throw new Error('Invalid response from server');
  },

  /**
   * Hydrate a food item's `image` array of IDs into resolved URLs.
   * Adds `imageUrls` (array) and `imageUrl` (first URL) on the returned copy.
   */
  resolveFoodItemImages: async (foodItem) => {
    if (!foodItem) return null;
    const resolved = { ...foodItem };
    if (foodItem.image && Array.isArray(foodItem.image)) {
      resolved.imageUrls = await fileService.downloadMultipleFiles(foodItem.image);
      if (resolved.imageUrls?.length > 0) {
        resolved.imageUrl = resolved.imageUrls[0];
      }
    } else if (foodItem.image) {
      resolved.imageUrl = await fileService.downloadFile(foodItem.image);
    }
    return resolved;
  },

  resolveImagesForCollection: async (items) => {
    if (!items || !Array.isArray(items)) return [];
    const resolved = await Promise.all(items.map((item) => fileService.resolveFoodItemImages(item)));
    return resolved.filter(Boolean);
  },
};

export default fileService;
